import { Injectable, inject } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { EventItemModel, EventService, LocCDType } from 'api';
import { getMonth } from 'common';
import { catchError, of, tap } from 'rxjs';

export class SetEventCodeStartAndEndDate {
  static readonly type = '[Ticketing Calendar] Set Start And End Date';
  constructor(
    public readonly eventCode: string,
    public readonly locationCode: LocCDType,
    public readonly startOfMonth: Date,
    public readonly endOfMonth: Date,
    public readonly eventEndDate: Date
  ) {}
}

export class SetHasProceededModal {
  static readonly type = '[Ticketing Calendar] Set Has Seen Modal';
  constructor(public hasProceeded: boolean) {}
}

export interface TicketingCalendarStateModel {
  eventCode: string | null;
  startOfMonth: Date | null;
  endOfMonth: Date | null;
  locationCode: LocCDType | null;
  reloading: boolean;
  eventMap: Record<string, EventItemModel[]> | null;
  hasProceededModal: boolean;
}

@State<TicketingCalendarStateModel>({
  name: 'ticketingCalendar',
  defaults: {
    eventCode: null,
    locationCode: null,
    startOfMonth: null,
    endOfMonth: null,
    reloading: false,
    eventMap: null,
    hasProceededModal: false,
  },
})
@Injectable()
export class TicketingCalendarState {
  private eventService = inject(EventService);

  @Action(SetEventCodeStartAndEndDate)
  setEventCodeStartAndEndDate(
    { patchState, getState }: StateContext<TicketingCalendarStateModel>,
    {
      eventCode,
      locationCode,
      startOfMonth,
      endOfMonth,
      eventEndDate,
    }: SetEventCodeStartAndEndDate
  ) {
    if (
      eventCode === getState().eventCode &&
      startOfMonth === getState().startOfMonth &&
      endOfMonth === getState().endOfMonth
    ) {
      return;
    }
    if (
      !eventCode ||
      !locationCode ||
      !startOfMonth ||
      !endOfMonth ||
      !eventEndDate
    ) {
      return;
    }
    let endDate = endOfMonth;
    if (getMonth(startOfMonth) === 11) {
      endDate = eventEndDate;
    }
    patchState({
      eventCode,
      startOfMonth,
      endOfMonth: endDate,
      reloading: true,
    });
    return this.eventService
      .getEventCalendar('OT', locationCode, eventCode, startOfMonth!, endDate)
      .pipe(
        tap((EventCalendarDetail) => {
          patchState({
            eventMap: EventCalendarDetail.eventMap,
            reloading: false,
          });
        }),
        catchError(() => {
          patchState({ reloading: false, eventMap: {} });
          return of(null);
        })
      );
  }

  @Action(SetHasProceededModal)
  setHasProceededModal(
    { patchState }: StateContext<TicketingCalendarStateModel>,
    { hasProceeded }: SetHasProceededModal
  ) {
    patchState({ hasProceededModal: hasProceeded });
  }
}

export class TicketingCalendarSelectors {
  @Selector([TicketingCalendarState])
  static eventMap(state: TicketingCalendarStateModel) {
    return state.eventMap;
  }

  @Selector([TicketingCalendarState])
  static reloading(state: TicketingCalendarStateModel) {
    return state.reloading;
  }
  @Selector([TicketingCalendarState])
  static hasProceededModal(state: TicketingCalendarStateModel): boolean {
    return state.hasProceededModal;
  }
}
